import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner, FullPageMessage } from '../ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, hasRole, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <Spinner fullPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <FullPageMessage
        icon={
          <svg className="h-8 w-8" style={{ color: 'var(--badge-red-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        }
        iconBg="var(--badge-red-bg)"
        title="Access Denied"
        description="You do not have the required permissions to access this page."
        extra={
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
            <p>Required role: <span className="font-medium">{requiredRole}</span></p>
            <p>Your role: <span className="font-medium">{user?.role}</span></p>
          </div>
        }
        action={
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-brand-600 bg-brand-100 hover:bg-brand-200 dark:text-brand-400 dark:bg-brand-900 dark:hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
          >
            Go Back
          </button>
        }
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
